"use client";

import { useState, type FC } from "react";
import { ShieldOffIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppEvent } from "@/lib/events";
import { setThreadAnonymization } from "@/lib/anonymization";
import { useAnonymizationStore } from "@/components/custom/stores/anonymizationStore";
import { ClientLogger } from "@/lib/clientLogger";

/**
 * Surfaced when the SSE stream emits ErrorEvent { code: "anonymization_unavailable" }.
 * The backend strict-blocks the turn (no PII leak), and we offer two paths forward:
 *  - Retry: just close the modal; the user can hit the retry affordance on the message
 *    (the turn was persisted as Interrupted by chatModelAdapters, so assistant-ui
 *    already shows the standard RetryCard).
 *  - Disable: PATCH the per-thread toggle to false and close the modal. Future turns
 *    in this thread go through unanonymized, allowing the user to keep working when the
 *    NER service is down for a longer stretch.
 *
 * Mirrors {@link ForbiddenActionDialog} in shape so the chrome stays consistent.
 */
export const AnonymizationErrorDialog: FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useAppEvent("anonymization-unavailable", (payload) => {
    setThreadId(payload.threadId);
    setOpen(true);
  });

  const onDisable = async () => {
    if (!threadId) {
      setOpen(false);
      return;
    }
    setPending(true);
    try {
      const fresh = await setThreadAnonymization(threadId, false);
      useAnonymizationStore.getState().set(threadId, fresh);
      setOpen(false);
    } catch (err) {
      ClientLogger.warn("Failed to disable anonymization from error dialog", {
        action: `PATCH /threads/${threadId}/anonymization`,
        component: "AnonymizationErrorDialog",
        error: err instanceof Error ? err : undefined,
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!pending) setOpen(o);
      }}
    >
      <DialogContent showCloseButton>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOffIcon className="size-5 text-destructive" />
            {t("anonymization.error.title")}
          </DialogTitle>
          <DialogDescription>
            {t("anonymization.error.message")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={pending}
          >
            {t("anonymization.error.retry")}
          </Button>
          <Button
            variant="destructive"
            onClick={onDisable}
            disabled={pending || !threadId}
          >
            {t("anonymization.error.disable")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
